import {type KeyboardEvent, useContext, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {Box, Chip, Collapse, Typography} from '@mui/material';

import {WorkforceCulturalAlignmentDimensions} from './WorkforceCulturalAlignment/WorkforceCulturalAlignmentDimensions';
import {WorkforceCulturalFitPillarDetailPanel} from './WorkforceCulturalFitPillarDetailPanel';
import {WorkforceCulturalFitPillarTab} from './WorkforceCulturalFitPillarTab';
import {WorkforceCulturalPillarsGrid} from './WorkforceCulturalPillarsGrid';
import {WorkforceCultureTypeOverviewCard} from './WorkforceCultureTypeOverviewCard';
import {
  culturalFitCollapsedCultureChipSx,
  culturalFitExpandIconSx,
  culturalFitPaperSx,
  culturalFitPillarTabRowSx,
  culturalFitSectionHeadingSx,
  getCulturalFitHeaderSx,
} from './workforceProfileCulturalFitAnalysisSectionSx';
import {EmpionPaper} from '../../../../../components/EmpionPaper/EmpionPaper';
import {InfoBanner} from '../../../../../components/InfoBanner/InfoBanner';
import {TableRowScorePill} from '../../../../../components/TableRowScorePill/TableRowScorePill';
import {LanguageContext} from '../../../../../contexts/LanguageContext/LanguageContext';
import {getLocalisedPropertyValue} from '../../../../../utils/getLocalisedPropertyValue';
import {WorkforceProfileCulturalFitAnalysis} from '../../types/WorkforceProfileTypes';
import {buildPillarDetailSections} from '../../utils/WorkforceProfileDetail/workforceProfileCulturalFitAnalysisUtils';

export interface Props {
  analysis: WorkforceProfileCulturalFitAnalysis;
  alwaysExpanded?: boolean;
}

export const WorkforceProfileCulturalFitAnalysisSection = ({analysis, alwaysExpanded}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'workforceProfilesPage.detail.culturalFitAnalysis'});
  const currentLanguage = useContext(LanguageContext);
  const [expanded, setExpanded] = useState(!!alwaysExpanded);
  const [activePillarIndex, setActivePillarIndex] = useState(0);

  const cultureTypeName = analysis.cultureType
    ? getLocalisedPropertyValue(analysis.cultureType, currentLanguage)
    : null;
  const fitScore = analysis.cultureFit?.fitScore;
  const isOpen = alwaysExpanded || expanded;

  const pillarDescriptions = analysis.pillarDescriptions;
  const hasTopQuestions = analysis.topQuestions.length > 0;
  const hasPillarDescriptions = pillarDescriptions.length > 0;
  const showCultureOverview = Boolean(cultureTypeName || analysis.cultureFit);

  const activePillar = pillarDescriptions[activePillarIndex];

  const pillarDetailSections = useMemo(() => {
    if (!activePillar) {
      return [];
    }

    return buildPillarDetailSections(activePillar, currentLanguage, {
      evaluations: t('evaluations'),
      opportunities: t('opportunities'),
      risks: t('risks'),
      whatToLookFor: t('whatToLookFor'),
      interviewQuestions: t('interviewQuestions'),
    });
  }, [activePillar, currentLanguage, t]);

  const pillarTabs = pillarDescriptions.map((pillar, index) => (
    <WorkforceCulturalFitPillarTab
      key={pillar.pillarTitle.en}
      pillar={pillar}
      language={currentLanguage}
      isActive={activePillarIndex === index}
      onSelect={() => setActivePillarIndex(index)}
    />
  ));

  const pillarDetailPanel = activePillar && (
    <WorkforceCulturalFitPillarDetailPanel
      key={activePillar.pillarTitle.en}
      sections={pillarDetailSections}
    />
  );

  const handleHeaderKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (alwaysExpanded) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setExpanded((previous) => !previous);
    }
  };

  const innerContent = (
    <Box>
      <InfoBanner text={t('description')} sx={{mb: 2.5}} />

      {showCultureOverview && (
        <WorkforceCultureTypeOverviewCard cultureTypeName={cultureTypeName} cultureFit={analysis.cultureFit} />
      )}

      <WorkforceCulturalPillarsGrid pillars={analysis.pillarData} />

      {hasTopQuestions && (
        <Box sx={{mb: 2.5}}>
          <Typography variant='subtitle2' sx={culturalFitSectionHeadingSx}>
            {t('topQuestions')}
          </Typography>
          <WorkforceCulturalAlignmentDimensions questions={analysis.topQuestions} />
        </Box>
      )}

      {hasPillarDescriptions && (
        <Box>
          <Typography variant='subtitle2' sx={culturalFitSectionHeadingSx}>
            {t('pillarDetails')}
          </Typography>
          <Box sx={culturalFitPillarTabRowSx}>{pillarTabs}</Box>
          {pillarDetailPanel}
        </Box>
      )}
    </Box>
  );

  const bodyContent = alwaysExpanded ? (
    innerContent
  ) : (
    <Collapse in={expanded} timeout='auto' unmountOnExit>
      {innerContent}
    </Collapse>
  );

  return (
    <EmpionPaper sx={culturalFitPaperSx}>
      <Box
        onClick={alwaysExpanded ? undefined : () => setExpanded((previous) => !previous)}
        onKeyDown={handleHeaderKeyDown}
        role={alwaysExpanded ? undefined : 'button'}
        tabIndex={alwaysExpanded ? undefined : 0}
        aria-expanded={alwaysExpanded ? undefined : expanded}
        sx={getCulturalFitHeaderSx(isOpen, !!alwaysExpanded)}
      >
        <Box sx={{flex: 1, minWidth: 0}}>
          <Typography variant='subtitle2' sx={{fontWeight: 600}}>
            {t('title')}
          </Typography>
          {!isOpen && (
            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', mt: 0.75}}>
              {cultureTypeName && (
                <Chip label={cultureTypeName} size='small' sx={culturalFitCollapsedCultureChipSx} />
              )}
              <Typography variant='caption' color='text.secondary' sx={{lineHeight: 1.45}}>
                {t('collapsedHint')}
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0}}>
          {!isOpen && fitScore != null && <TableRowScorePill score={fitScore} />}
          {!alwaysExpanded && <ExpandMoreIcon sx={culturalFitExpandIconSx(expanded)} />}
        </Box>
      </Box>

      {bodyContent}
    </EmpionPaper>
  );
};
